import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * PageLoader
 * ──────────
 * Full-page cold-visit intro. Reuses the Power-X arrows crossing animation,
 * then fades to reveal the site. Runs ONCE per session (sessionStorage flag)
 * so the loader never interrupts internal navigation or a returning visitor.
 *
 * Timing budget: 0.05s stagger in → 0.55s arrows travel → 0.35s hold →
 * 0.45s fade out = ~1.4s max cold visit, gracefully degraded via reduced-motion.
 */
export default function PageLoader() {
  // Initialise once — read sessionStorage in the lazy initialiser so
  // React StrictMode's double-invoked effects don't race with the flag.
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      if (sessionStorage.getItem("ix.pageloader.seen") === "1") return false;
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return false;
      sessionStorage.setItem("ix.pageloader.seen", "1");
      return true;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (!visible) return undefined;
    // Lock scroll while intro plays so hero content doesn't pop-in behind it.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setVisible(false);
      document.body.style.overflow = prev;
    }, 1050);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [visible]);

  const arrow = (angle, delay) => {
    const a = (angle * Math.PI) / 180;
    const from = { x: Math.cos(a) * -160, y: Math.sin(a) * -160 };
    return (
      <motion.g
        key={angle}
        initial={{ opacity: 0, x: from.x, y: from.y }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.55, delay, ease: [0.76, 0, 0.24, 1] }}
      >
        <line
          x1="128"
          y1="128"
          x2={128 + Math.cos(a) * 78}
          y2={128 + Math.sin(a) * 78}
          stroke="#E8FF00"
          strokeWidth="14"
          strokeLinecap="butt"
          markerEnd="url(#loader-ah)"
        />
      </motion.g>
    );
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="ix-loader"
          data-testid="page-loader"
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ backgroundColor: "#1A083E" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* subtle grain / vignette for depth */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-40 mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(closest-side at 50% 50%, rgba(124,58,237,0.65), transparent 70%)",
            }}
          />
          <svg
            width="220"
            height="220"
            viewBox="0 0 256 256"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <defs>
              <marker
                id="loader-ah"
                viewBox="0 0 6 6"
                refX="1.2"
                refY="3"
                markerWidth="4"
                markerHeight="4"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path d="M0,0 L6,3 L0,6 L2,3 Z" fill="#E8FF00" />
              </marker>
            </defs>
            {arrow(-135, 0.05)}
            {arrow(-45, 0.1)}
            {arrow(135, 0.15)}
            {arrow(45, 0.2)}
            <motion.circle
              cx="128"
              cy="128"
              r="12"
              fill="#E8FF00"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.35, delay: 0.55, ease: [0.76, 0, 0.24, 1] }}
              style={{ transformOrigin: "128px 128px" }}
            />
          </svg>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.75 }}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 text-center"
          >
            <div className="text-[10px] tracking-[0.32em] uppercase text-lime/80 font-semibold">
              Instantxfer · CashApp
            </div>
            <div className="mt-2 text-canvas/50 text-sm">
              money in <em className="font-serif italic text-canvas/70">motion</em>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
